import { JSX, useMemo, useCallback } from "react";

import { PushPin } from "@mui/icons-material";
import { List, Collapse, Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useLocationsDataQuery } from "src/api/locationQueries.api";
import { useAppErrorStore } from "src/store/error.store";
import useAppPinnedStore from "src/store/pinned.store";
import { useSidebarExpandStore } from "src/store/sidebar.store";
import LocationType from "src/types/LocationType";

import { getDestinationRouteFromLocationType, getStyles } from "../sidebar.helpers";
import PinnedLocationRow from "./PinnedLocationRow";
import SectionHeader from "./SectionHeader";

export type Props = {
  open: boolean;
};

function SidebarPinnedList({ open }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { appPinned } = useAppPinnedStore();
  const { data: locations } = useLocationsDataQuery();
  const { setError } = useAppErrorStore();

  const { expand, setExpand, setLocationId } = useSidebarExpandStore();

  const isExpanded = expand["Pinned"]?.expand ?? false;

  const pinnedLocationIds = useMemo(() => appPinned.map((loc) => loc.id), [appPinned]);

  const displayedLocations = useMemo(() => {
    if (!locations) return [];

    const pinned = locations.value.filter((loc) => pinnedLocationIds.includes(loc.Id));

    return pinned.sort((a, b) => {
      const typeA = LocationType[a.Type as keyof typeof LocationType];
      const typeB = LocationType[b.Type as keyof typeof LocationType];

      return typeA === typeB ? a.LocalName.localeCompare(b.LocalName) : typeA < typeB ? -1 : 1;
    });
  }, [locations, pinnedLocationIds]);

  const onToggleExpand = useCallback(() => setExpand("Pinned", !isExpanded), [setExpand, isExpanded]);

  const onSelectLocation = useCallback(
    (id: string, locationType: LocationType) => {
      setLocationId("Pinned", id);
      navigate(getDestinationRouteFromLocationType(locationType, id));
    },
    [navigate, setLocationId]
  );

  if (displayedLocations.length === 0) return null;

  return (
    <List>
      <SectionHeader
        open={open}
        canNavigate={false}
        isExpanded={isExpanded}
        toggleExpand={onToggleExpand}
        label={t("Pinned")}
        onClick={() => {}}
        icon={<PushPin />}
        showToggle={true}
      />

      <Collapse in={isExpanded} timeout='auto' unmountOnExit>
        <Box sx={getStyles(theme).border}>
          {displayedLocations.map((location) => (
            <PinnedLocationRow
              key={location.Id}
              open={open}
              onSelect={onSelectLocation}
              pathname={pathname}
              setError={setError}
              parent={locations?.value.find((loc) => loc.Id === location.ParentId)}
              location={location}
            />
          ))}
        </Box>
      </Collapse>
    </List>
  );
}

export default SidebarPinnedList;
